import React, { useEffect, useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Lock, ArrowRight, CheckCircle2, Loader2, XCircle, Eye, EyeOff } from 'lucide-react';
import { BRAND } from '../mock/mock';
import { api } from '../lib/api';

const AdminResetPassword = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') || '';
  const [status, setStatus] = useState('checking');
  const [email, setEmail] = useState('');
  const [form, setForm] = useState({ new_password: '', confirm: '' });
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) { setStatus('invalid'); return; }
    (async () => {
      try {
        const { data } = await api.get(`/admin/verify-reset-token?token=${encodeURIComponent(token)}`);
        if (data.valid) { setStatus('valid'); setEmail(data.email); }
        else { setStatus('invalid'); setError('Token invalid'); }
      } catch {
        setStatus('invalid'); setError('Unable to verify token');
      }
    })();
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (form.new_password.length < 8) { setError('Password must be at least 8 characters'); return; }
    if (form.new_password !== form.confirm) { setError('Passwords do not match'); return; }
    setError(''); setBusy(true);
    try {
      await api.post('/admin/reset-password', { token, new_password: form.new_password });
      setDone(true);
      setTimeout(() => navigate('/admin-login'), 2500);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Reset failed');
    } finally { setBusy(false); }
  };

  return (
    <main className="min-h-screen relative overflow-hidden flex items-center py-12">
      <div className="absolute inset-0 -z-10">
        <div className="absolute inset-0 bg-gradient-to-b from-[#f7fbf3] via-[#fdfaf1] to-[#f4f9ef]" />
        <div className="absolute -top-40 -right-40 h-[520px] w-[520px] rounded-full bg-[#F0B93C]/15 blur-3xl" />
        <div className="absolute -bottom-40 -left-40 h-[520px] w-[520px] rounded-full bg-[#0B5E2A]/15 blur-3xl" />
      </div>
      <div className="max-w-md mx-auto container-px w-full">
        <div className="bg-white border border-[#0B5E2A]/10 rounded-2xl p-8 shadow-[0_20px_50px_-25px_rgba(11,94,42,0.25)]">
          <div className="text-center">
            <div className="mx-auto h-14 w-14 rounded-full bg-[#06401d] flex items-center justify-center">
              <Lock className="h-7 w-7 text-[#F0B93C]" />
            </div>
            <h1 className="mt-4 font-display text-2xl text-[#0B5E2A]">Reset Admin Password</h1>
            <p className="mt-2 text-sm text-[#4b5e4d]">{BRAND.name} · Admin Portal</p>
          </div>

          {status === 'checking' && (<div className="mt-8 text-center"><Loader2 className="h-6 w-6 mx-auto animate-spin text-[#6b7e6d]" /><p className="mt-2 text-sm text-[#6b7e6d]">Verifying link...</p></div>)}

          {status === 'invalid' && (
            <div className="mt-6 text-center">
              <XCircle className="h-10 w-10 mx-auto text-[#9b1c1c]" />
              <div className="mt-3 font-display text-lg text-[#9b1c1c]">Link Invalid or Expired</div>
              <p className="mt-2 text-sm text-[#4b5e4d]">{error || 'This password reset link is no longer valid.'}</p>
              <Link to="/admin/forgot-password" className="mt-6 btn-green inline-flex items-center gap-2 px-5 py-2.5 rounded-md text-sm">Request a new link</Link>
            </div>
          )}

          {status === 'valid' && !done && (
            <form onSubmit={submit} className="mt-6 space-y-4">
              <p className="text-sm text-[#4b5e4d]">Set a new password for <span className="font-semibold">{email}</span>.</p>
              <div>
                <label className="block text-sm font-medium text-[#2e4030] mb-1.5">New Password</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#6b7e6d]" />
                  <input type={show ? 'text' : 'password'} value={form.new_password} onChange={(e) => setForm({ ...form, new_password: e.target.value })} data-testid="admin-reset-newpw" className="w-full pl-10 pr-10 py-2.5 rounded-md border border-[#0B5E2A]/20 text-sm focus:outline-none focus:ring-2 focus:ring-[#0B5E2A]/30" />
                  <button type="button" onClick={() => setShow(!show)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#6b7e6d] hover:text-[#0B5E2A]">{show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</button>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-[#2e4030] mb-1.5">Confirm Password</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#6b7e6d]" />
                  <input type={show ? 'text' : 'password'} value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })} data-testid="admin-reset-confirm" className="w-full pl-10 pr-3 py-2.5 rounded-md border border-[#0B5E2A]/20 text-sm focus:outline-none focus:ring-2 focus:ring-[#0B5E2A]/30" />
                </div>
              </div>
              {error && <div className="text-sm text-[#9b1c1c] bg-[#FDE2E2] rounded p-2">{error}</div>}
              <button type="submit" disabled={busy} data-testid="admin-reset-submit" className="btn-green w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-md disabled:opacity-60">
                {busy ? (<><Loader2 className="h-4 w-4 animate-spin" /> Updating...</>) : (<>Set New Password <ArrowRight className="h-4 w-4" /></>)}
              </button>
            </form>
          )}

          {done && (
            <div className="mt-6 text-center">
              <CheckCircle2 className="h-10 w-10 mx-auto brand-green" />
              <div className="mt-3 font-display text-lg text-[#0B5E2A]">Password Updated</div>
              <p className="mt-2 text-sm text-[#4b5e4d]">Redirecting you to login...</p>
            </div>
          )}
        </div>
      </div>
    </main>
  );
};

export default AdminResetPassword;
